import { Link } from "react-router-dom";
import "@/App.css";
import SEO from "../components/shared/SEO";
import { 
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Search,
  Settings,
  Rocket,
  ChevronRight
} from "lucide-react";

// Navigation Component
const Navigation = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-1.5 rounded-full bg-[#D4AF37]"
                style={{ height: `${12 + i * 6}px` }}
              />
            ))}
          </div>
          <div>
            <span className="text-white font-bold tracking-tight">LRS</span>
            <span className="hidden sm:block text-[10px] text-zinc-500 uppercase tracking-widest">Leitner Revenue Systems</span>
          </div>
        </Link>
        
        <Link
          to="/"
          className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  </nav>
);

// Footer Component
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-white/5 py-12">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-1.5 rounded-full bg-[#D4AF37]"
                style={{ height: `${8 + i * 4}px` }}
              />
            ))}
          </div>
          <span className="text-white font-bold">LRS</span>
        </div>
        <p className="text-sm text-zinc-500">
          © {new Date().getFullYear()} Leitner Revenue Systems. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// The LRS Revenue System Framework
const FrameworkSection = () => (
  <section className="py-24 lg:py-32 bg-[#0A0A0A]">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-14">
        <div className="text-sm font-mono text-[#D4AF37] tracking-widest uppercase mb-4">Framework</div>
        <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-4">
          The Leitner Revenue System
        </h2>
        <p className="text-zinc-400 max-w-xl mx-auto">
          A repeatable framework for building revenue infrastructure that scales.
        </p>
      </div>

      {/* Visual Flow Diagram */}
      <div className="relative py-8">
        {/* Connection Line - Desktop */}
        <div className="hidden lg:block absolute top-1/2 left-[18%] right-[18%] h-[3px] bg-gradient-to-r from-[#D4AF37]/30 via-[#D4AF37] to-[#D4AF37]/30 -translate-y-1/2 z-0" />
        
        {/* Chevron connectors - Desktop */}
        <div className="hidden lg:flex absolute top-1/2 left-[36%] -translate-y-1/2 z-20">
          <ChevronRight className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div className="hidden lg:flex absolute top-1/2 right-[36%] -translate-y-1/2 z-20">
          <ChevronRight className="w-6 h-6 text-[#D4AF37]" />
        </div>
        
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-6 relative z-10">
          {/* Step 1 */}
          <div className="flex flex-col items-center text-center bg-[#1C1C1C] rounded-2xl p-8 border border-white/5">
            <div className="w-16 h-16 rounded-2xl bg-[#0A0A0A] border-2 border-[#D4AF37] flex items-center justify-center mb-5 shadow-lg shadow-[#D4AF37]/10">
              <Search className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div className="text-sm font-mono text-[#D4AF37] mb-2">Step 1</div>
            <h3 className="text-xl font-bold text-white mb-3">Diagnose</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Identify where targeting, messaging, process, or conversion is breaking down.
            </p>
          </div>

          {/* Step 2 */}
          <div className="flex flex-col items-center text-center bg-[#1C1C1C] rounded-2xl p-8 border border-white/5">
            <div className="w-16 h-16 rounded-2xl bg-[#0A0A0A] border-2 border-[#D4AF37] flex items-center justify-center mb-5 shadow-lg shadow-[#D4AF37]/10">
              <Settings className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div className="text-sm font-mono text-[#D4AF37] mb-2">Step 2</div>
            <h3 className="text-xl font-bold text-white mb-3">Rebuild</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Redesign workflows, SDR motion, and GTM alignment for higher performance.
            </p>
          </div>

          {/* Step 3 */}
          <div className="flex flex-col items-center text-center bg-gradient-to-b from-[#D4AF37]/10 to-[#1C1C1C] rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37] flex items-center justify-center mb-5 shadow-lg shadow-[#D4AF37]/30">
              <Rocket className="w-7 h-7 text-[#0A0A0A]" />
            </div>
            <div className="text-sm font-mono text-[#D4AF37] mb-2">Step 3</div>
            <h3 className="text-xl font-bold text-white mb-3">Install</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Deploy AI augmentation, talent systems, and repeatable infrastructure.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section id="services" className="py-24 lg:py-32 bg-[#1C1C1C]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-14">
        <div className="text-sm font-mono text-[#D4AF37] tracking-widest uppercase mb-3">Services</div>
        <p className="text-zinc-400">I don't optimize around the edges. I fix the system.</p>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        {/* What We Fix */}
        <div>
          <div className="mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-white">What I Fix</h2>
            <p className="text-sm text-zinc-500 mt-2">Common system failures I diagnose and repair.</p>
          </div>
          <div className="space-y-3">
            {[
              { id: "inconsistent-pipeline", text: "Inconsistent pipeline generation", detail: "Activity without predictable results" },
              { id: "sdr-no-direction", text: "SDR motion without direction", detail: "High effort, low conversion" },
              { id: "over-reliance-inbound", text: "Over-reliance on inbound", detail: "No control over pipeline creation" },
              { id: "broken-gtm", text: "Broken GTM handoffs", detail: "Leads lost between teams" }
            ].map((item) => (
              <div key={item.id} className="flex items-start gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-white/5 hover:border-white/10 transition-colors">
                <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                <div>
                  <span className="text-white font-medium">{item.text}</span>
                  <p className="text-xs text-zinc-500 mt-1">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What We Build */}
        <div>
          <div className="mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-white">What I Build</h2>
            <p className="text-sm text-zinc-500 mt-2">Infrastructure that creates leverage.</p>
          </div>
          <div className="space-y-3">
            {[
              { id: "ai-sdr", text: "AI-powered SDR systems", detail: "Scale outreach without losing quality" },
              { id: "scalable-infra", text: "Scalable revenue infrastructure", detail: "Processes that compound" },
              { id: "talent-engine", text: "SDR-to-AE talent engines", detail: "Develop closers internally" },
              { id: "predictable-gtm", text: "Predictable GTM systems", detail: "Pipeline you can forecast" }
            ].map((item) => (
              <div key={item.id} className="flex items-start gap-4 p-4 bg-gradient-to-r from-[#D4AF37]/10 to-transparent rounded-lg border border-[#D4AF37]/20 hover:border-[#D4AF37]/30 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-white font-medium">{item.text}</span>
                  <p className="text-xs text-zinc-500 mt-1">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-20 bg-[#0A0A0A]">
    <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
      <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
        Ready to Fix Your Revenue System?
      </h2>
      <p className="text-zinc-400 mb-8">
        Let's diagnose what's broken and build infrastructure that scales.
      </p>
      <a
        href="https://calendly.com/leitner-revenue-systems/30min"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-semibold hover:bg-[#E8C547] transition-all"
      >
        Get Started
        <ArrowRight className="w-5 h-5" />
      </a>
    </div>
  </section>
);

// Main Services Page
function ServicesPage() {
  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEO 
        title="Revenue Services | GTM Strategy & Execution"
        description="I build revenue infrastructure that scales. GTM strategy, SDR team design, and revenue system implementation for Series B & C startups."
        path="/services"
        image="services"
      />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-sm font-mono text-[#D4AF37] tracking-widest uppercase mb-4">Services</div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Revenue Systems That Scale
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            I build the infrastructure that turns SDR teams into predictable pipeline engines.
          </p>
        </div>
      </section>
      
      <FrameworkSection />
      <ServicesSection />
      <CTASection />
      <Footer />
    </div>
  );
}

export default ServicesPage;
